import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const sectorColors = {
  retail: "var(--color-primary)",
  tech: "#A78BFA",
  manufacturing: "var(--color-accent)",
  financial: "var(--color-success)",
  black_market: "var(--color-error)",
};

const sectorIcons = {
  retail: "ShoppingCart",
  tech: "Cpu",
  manufacturing: "Factory",
  financial: "Landmark",
  black_market: "Skull",
};

const riskColors = {
  Low: "var(--color-success)",
  Medium: "var(--color-accent)",
  High: "var(--color-error)",
};

const AcquisitionModal = ({ business, playerCash, onConfirm, onClose }) => {
  const [confirmed, setConfirmed] = useState(false);
  const canAfford = playerCash >= business?.purchasePrice;
  const sectorColor = sectorColors?.[business?.sector] || "var(--color-primary)";
  const sectorIcon = sectorIcons?.[business?.sector] || "Building2";

  const handleConfirm = () => {
    if (!canAfford) return;
    setConfirmed(true);
    setTimeout(() => {
      onConfirm(business);
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }}>
      <div className="w-full max-w-md rounded-2xl border overflow-hidden" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--color-border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${sectorColor}15`, border: `1px solid ${sectorColor}30` }}>
              <Icon name={sectorIcon} size={18} color={sectorColor} />
            </div>
            <div>
              <h3 className="font-heading text-sm font-bold text-foreground">{business?.name}</h3>
              <span className="font-caption text-xs capitalize" style={{ color: sectorColor }}>{business?.sector?.replace("_", " ")}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">{business?.description}</p>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Purchase Price", value: `$${business?.purchasePrice?.toLocaleString()}`, icon: "DollarSign", color: "var(--color-error)" },
              { label: "Expected Revenue", value: `$${business?.expectedRevenue?.toLocaleString()}/mo`, icon: "TrendingUp", color: "var(--color-success)" },
              { label: "Operating Cost", value: `$${business?.operatingCost?.toLocaleString()}/mo`, icon: "Minus", color: "var(--color-accent)" },
              { label: "ROI Period", value: business?.roiPeriod, icon: "Clock", color: "var(--color-primary)" },
            ]?.map(item => (
              <div key={item?.label} className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.03)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name={item?.icon} size={11} color={item?.color} />
                  <span className="font-caption text-xs text-muted-foreground">{item?.label}</span>
                </div>
                <div className="font-heading text-sm font-semibold" style={{ color: item?.color }}>{item?.value}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={13} color={riskColors?.[business?.risk]} />
              <span className="font-caption text-xs text-muted-foreground">Risk Level</span>
            </div>
            <span className="font-caption text-xs font-semibold" style={{ color: riskColors?.[business?.risk] }}>{business?.risk}</span>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border" style={{ background: canAfford ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)", borderColor: canAfford ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)" }}>
            <div className="flex items-center gap-2">
              <Icon name="Wallet" size={13} color={canAfford ? "var(--color-success)" : "var(--color-error)"} />
              <span className="font-caption text-xs text-muted-foreground">Your Cash</span>
            </div>
            <span className="font-caption text-xs font-semibold" style={{ color: canAfford ? "var(--color-success)" : "var(--color-error)" }}>
              ${playerCash?.toLocaleString()} {!canAfford && "(Insufficient)"}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t" style={{ borderColor: "var(--color-border)" }}>
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
          <Button
            variant={confirmed ? "success" : "default"}
            fullWidth
            disabled={!canAfford}
            loading={confirmed}
            iconName={confirmed ? "Check" : "ShoppingBag"}
            iconPosition="left"
            onClick={handleConfirm}
          >
            {confirmed ? "Purchasing..." : "Purchase Business"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AcquisitionModal;